import axios from 'axios';
import { subscribe } from '@/cable';

let subscription = null;
const BASE = '/api/v1/telephony/incoming_calls';
const live = (item, now, companyId) => item && String(item.company_id) === String(companyId) && Date.parse(item.expires_at) > now;
const auth = (getters) => ({ headers: { Authorization: getters.getAuthToken } });

export default {
  namespaced: true,
  state: () => ({ items: [], dismissed: [], companyId: null, version: 0, revision: 0, snapshot: 0, now: Date.now() }),
  getters: { visible: (state) => state.items.filter((item) => live(item, state.now, state.companyId)).slice(0, 3) },
  mutations: {
    RESET(state) { state.items = []; state.dismissed = []; state.companyId = null; state.version += 1; },
    CONTEXT(state, companyId) { state.companyId = companyId; state.now = Date.now(); },
    EVENT(state) { state.revision += 1; },
    SNAPSHOT(state) { state.snapshot += 1; },
    TICK(state, now) {
      state.now = now;
      state.items = state.items.filter((item) => live(item, now, state.companyId));
      state.dismissed = state.dismissed.filter((item) => item.expiresAt > now);
    },
    EMPTY(state) { state.items = []; },
    REMOVE(state, id) { state.items = state.items.filter((item) => String(item.id) !== String(id)); },
    CLOSE(state, id) {
      state.dismissed.push({ id, expiresAt: Date.now() + 60000 });
      state.items = state.items.filter((item) => String(item.id) !== String(id));
    },
    DISMISS(state, id) {
      const item = state.items.find((entry) => String(entry.id) === String(id));
      if (item) state.dismissed.push({ id, expiresAt: Date.parse(item.expires_at) });
      state.items = state.items.filter((entry) => String(entry.id) !== String(id));
    },
    UPSERT(state, item) {
      if (!live(item, Date.now(), state.companyId) || state.dismissed.some((entry) => String(entry.id) === String(item.id))) return;
      state.items = [item, ...state.items.filter((entry) => entry.id !== item.id)]
        .sort((a, b) => Date.parse(b.expires_at) - Date.parse(a.expires_at) || b.id - a.id).slice(0, 20);
    },
  },
  actions: {
    stop({ commit }) {
      if (subscription) subscription.unsubscribe();
      subscription = null;
      commit('RESET');
    },
    async start({ dispatch, commit, state }, { companyId }) {
      dispatch('stop');
      commit('CONTEXT', companyId);
      const version = state.version;
      subscription = subscribe({ channel: 'TelephonyChannel' }, {
        connected: () => dispatch('refresh', version),
        rejected: () => { if (version === state.version) commit('EMPTY'); },
        received: (data) => dispatch('receive', { data, version }),
      });
      await dispatch('refresh', version);
    },
    async refresh({ state, commit, rootGetters }, version) {
      if (version !== state.version || !state.companyId) return;
      const revision = state.revision;
      commit('SNAPSHOT');
      const snapshot = state.snapshot;
      try {
        const { data } = await axios.get(BASE, auth(rootGetters));
        if (version !== state.version || snapshot !== state.snapshot) return;
        if (revision === state.revision) commit('EMPTY');
        (data.calls || []).slice().reverse().forEach((item) => commit('UPSERT', item));
      } catch (error) {
        if (version === state.version && snapshot === state.snapshot &&
          (revision === state.revision || [401, 403].includes(error.response && error.response.status))) commit('EMPTY');
      }
    },
    configurationChanged({ state, dispatch }, data) {
      if (state.companyId && (!data || String(data.company_id) === String(state.companyId))) {
        return dispatch('start', { companyId: state.companyId });
      }
    },
    async receive({ state, commit, rootGetters }, { data, version }) {
      if (version !== state.version || !data || String(data.company_id) !== String(state.companyId)) return;
      commit('EVENT');
      if (!live(data, Date.now(), state.companyId)) { commit('CLOSE', data.id); return; }
      if (!/^\d+$/.test(String(data.id))) return;
      try {
        const response = await axios.get(`${BASE}/${data.id}`, auth(rootGetters));
        if (version === state.version) commit('UPSERT', response.data);
      } catch (error) {
        if (version !== state.version) return;
        if ([401, 403].includes(error.response && error.response.status)) commit('EMPTY');
        else commit('REMOVE', data.id);
      }
    },
    dismiss({ commit }, id) { commit('DISMISS', id); },
  },
};
